/**
 * OKR 캐시/동기화 헬퍼
 *
 * @description OKR 캐시 키 생성, 캐시 초기화, 이름/상태 동기화
 *              useOkrStore에서 추출된 순수 캐시 조작 함수들
 *
 * @usage
 * const sync = useOkrSync({ kpiOkrMap, krPlanMap, ... });
 * sync.syncKrNameInAllCaches(krId, newName);
 */
import type { Ref } from 'vue';
import type {
  OkrGrouped,
  KpiOkrMapObjective,
  PlanResponseItem,
} from './useKpiTypes';

// ============================================
// 의존성 타입 정의
// ============================================

interface OkrSyncDeps {
  kpiOkrMap: Record<string, KpiOkrMapObjective[]>;
  krPlanMap: Record<string, PlanResponseItem[]>;
  kpiTaskMap: Record<string, PlanResponseItem[]>;
  okrListForMember: Ref<OkrGrouped[]>;
  unlinkedOkrList: Ref<KpiOkrMapObjective[]>;
  expandedKpiIds: Set<string>;
  expandedKrIds: Set<string>;
  selectedCycleId: Ref<string>;
}

// ============================================
// Composable
// ============================================

export const useOkrSync = (deps: OkrSyncDeps) => {
  const {
    kpiOkrMap,
    krPlanMap,
    kpiTaskMap,
    okrListForMember,
    unlinkedOkrList,
    expandedKpiIds,
    expandedKrIds,
    selectedCycleId,
  } = deps;

  /**
   * 캐시 키 생성 헬퍼 (분기별 캐시 분리)
   */
  const getOkrCacheKey = (kpiId: string) => `${kpiId}_${selectedCycleId.value || 'all'}`;
  const getKrPlanCacheKey = (krId: string) => `${krId}_${selectedCycleId.value || 'all'}`;

  /**
   * 목표-업무 연계도 캐시 초기화
   */
  const clearGoalOkrCache = () => {
    expandedKpiIds.clear();
    expandedKrIds.clear();
    Object.keys(kpiOkrMap).forEach(key => delete kpiOkrMap[key]);
    Object.keys(krPlanMap).forEach(key => delete krPlanMap[key]);
    Object.keys(kpiTaskMap).forEach(key => delete kpiTaskMap[key]);
    unlinkedOkrList.value = [];
  };

  /**
   * OBJ 이름 변경 시 모든 캐시 동기화
   * @param categoryId - Objective ID
   * @param newCategoryNm - 새로운 Objective 이름
   */
  const syncObjNameInAllCaches = (categoryId: string, newCategoryNm: string) => {
    // 1. kpiOkrMap 캐시 업데이트
    Object.keys(kpiOkrMap).forEach(key => {
      const okrList = kpiOkrMap[key];
      if (Array.isArray(okrList)) {
        const targetObj = okrList.find(o => o.categoryId === categoryId);
        if (targetObj) {
          targetObj.categoryNm = newCategoryNm;
        }
      }
    });

    // 2. okrListForMember 업데이트 (센터패널)
    okrListForMember.value.forEach(obj => {
      if (obj.objId === categoryId) {
        obj.objNm = newCategoryNm;
      }
    });

    // 3. unlinkedOkrList 업데이트 (미연계 OKR)
    unlinkedOkrList.value.forEach(obj => {
      if (obj.categoryId === categoryId) {
        obj.categoryNm = newCategoryNm;
      }
    });
  };

  /**
   * KR 이름 변경 시 모든 캐시 동기화
   * @param krId - KR ID (planId)
   * @param newKrNm - 새로운 KR 이름
   */
  const syncKrNameInAllCaches = (krId: string, newKrNm: string) => {
    // 1. kpiOkrMap 캐시 업데이트
    Object.keys(kpiOkrMap).forEach(key => {
      const okrList = kpiOkrMap[key];
      if (Array.isArray(okrList)) {
        okrList.forEach(obj => {
          const targetKr = obj.keyResults?.find(kr => kr.planId === krId || kr.krId === krId);
          if (targetKr) {
            targetKr.planNm = newKrNm;
            if (targetKr.krNm !== undefined) {
              targetKr.krNm = newKrNm;
            }
          }
        });
      }
    });

    // 2. okrListForMember 업데이트 (센터패널)
    okrListForMember.value.forEach(obj => {
      const targetKr = obj.keyResults?.find(kr => kr.krId === krId);
      if (targetKr) {
        targetKr.krNm = newKrNm;
      }
    });

    // 3. unlinkedOkrList 업데이트 (미연계 OKR)
    unlinkedOkrList.value.forEach(obj => {
      const targetKr = obj.keyResults?.find(kr => kr.planId === krId);
      if (targetKr) {
        targetKr.planNm = newKrNm;
      }
    });
  };

  /**
   * KR 상태/달성률 변경 시 모든 캐시 동기화
   * @param krId - KR ID
   * @param updates - 업데이트할 필드들
   */
  const syncKrStatusInAllCaches = (krId: string, updates: {
    achive?: number | string;
    actual?: number | string;
    statusCd?: string;
    statusColor?: string;
    progress?: number;
  }) => {
    // 1. kpiOkrMap 캐시 업데이트
    Object.keys(kpiOkrMap).forEach(key => {
      const okrList = kpiOkrMap[key];
      if (Array.isArray(okrList)) {
        okrList.forEach(obj => {
          const targetKr = obj.keyResults?.find(kr => kr.planId === krId || kr.krId === krId);
          if (targetKr) {
            if (updates.achive !== undefined) targetKr.achive = updates.achive;
            if (updates.actual !== undefined) targetKr.actual = updates.actual;
            if (updates.statusCd !== undefined) targetKr.statusCd = updates.statusCd;
            if (updates.statusColor !== undefined) {
              targetKr.statusColor = updates.statusColor;
              targetKr.color = updates.statusColor;
            }
            if (updates.progress !== undefined) targetKr.progress = updates.progress;
          }
        });
      }
    });

    // 2. okrListForMember 업데이트 (센터패널)
    okrListForMember.value.forEach(obj => {
      const targetKr = obj.keyResults?.find(kr => kr.krId === krId);
      if (targetKr) {
        if (updates.progress !== undefined) targetKr.progress = updates.progress;
        if (updates.actual !== undefined) targetKr.actual = updates.actual;
        if (updates.statusColor !== undefined) targetKr.statusColor = updates.statusColor;
      }
    });

    // 3. unlinkedOkrList 업데이트 (미연계 OKR)
    unlinkedOkrList.value.forEach(obj => {
      const targetKr = obj.keyResults?.find(kr => kr.planId === krId);
      if (targetKr) {
        if (updates.achive !== undefined) targetKr.achive = updates.achive;
        if (updates.actual !== undefined) targetKr.actual = updates.actual;
        if (updates.statusCd !== undefined) targetKr.statusCd = updates.statusCd;
        if (updates.statusColor !== undefined) {
          targetKr.statusColor = updates.statusColor;
          targetKr.color = updates.statusColor;
        }
      }
    });
  };

  /**
   * KR NEW 뱃지 동기화 — 모달에서 피드백 읽음 처리 후 KR 행 뱃지 즉시 반영
   * (목록 재조회 없이 kpiOkrMap / okrListForMember / unlinkedOkrList 일괄 갱신)
   */
  const syncKrNewYnInAllCaches = (krId: string, newYn: 'Y' | 'N') => {
    // 1. kpiOkrMap 캐시 (KPI 연결 KR)
    Object.keys(kpiOkrMap).forEach(key => {
      const okrList = kpiOkrMap[key];
      if (Array.isArray(okrList)) {
        okrList.forEach(obj => {
          const targetKr = obj.keyResults?.find(kr => kr.planId === krId || kr.krId === krId);
          if (targetKr) targetKr.newYn = newYn;
        });
      }
    });

    // 2. okrListForMember (센터패널)
    okrListForMember.value.forEach(obj => {
      const targetKr = obj.keyResults?.find(kr => kr.krId === krId);
      if (targetKr) targetKr.newYn = newYn;
    });

    // 3. unlinkedOkrList (미연계 OKR)
    unlinkedOkrList.value.forEach(obj => {
      const targetKr = obj.keyResults?.find(kr => kr.planId === krId || kr.krId === krId);
      if (targetKr) targetKr.newYn = newYn;
    });
  };

  return {
    getOkrCacheKey,
    getKrPlanCacheKey,
    clearGoalOkrCache,
    syncObjNameInAllCaches,
    syncKrNameInAllCaches,
    syncKrStatusInAllCaches,
    syncKrNewYnInAllCaches,
  };
};
